import { Link, usePathname } from "expo-router";
import { useMemo, type JSX } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { fontFamily, layout, radius, space } from "@passfirst/ui";

import { Lockup } from "./lockup";
import { NAV_ITEMS, pathToTab } from "./nav-items";
import { type Colors, useColors } from "./theme";

interface Props {
  /** Bottom user chip — primary line (e.g. email or name). */
  userLabel: string;
  /** Bottom user chip — secondary line (e.g. "CDL · California"). */
  metaLabel?: string;
}

// Persistent 236px left sidebar for the web/wide product surface
// (design_handoff_2, Rule B). Lockup top, the four NAV_ITEMS as a
// vertical list (active = navySoft pill + soft-navy text + bold), and a
// user chip pinned to the bottom.
export function AppSidebar({ userLabel, metaLabel }: Props): JSX.Element {
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const pathname = usePathname();
  const active = pathToTab(pathname);
  const initial = userLabel.trim().charAt(0).toUpperCase() || "?";

  return (
    <View style={styles.sidebar}>
      <View style={styles.lockupWrap}>
        <Lockup size={26} />
      </View>

      <View style={styles.nav} accessibilityRole="tablist">
        {NAV_ITEMS.map((item) => {
          const isActive = active === item.id;
          const tint = isActive ? colors.accentSoftText : colors.textSecondary;
          return (
            <Link key={item.id} href={item.href} asChild>
              <Pressable
                accessibilityRole="tab"
                accessibilityState={{ selected: isActive }}
                accessibilityLabel={item.label}
                // Flatten: <Link asChild> renders this through expo-router's
                // <Slot>, which throws in dev when a direct child's `style` is
                // an array. Flattening keeps the merged style as one object.
                style={StyleSheet.flatten([
                  styles.navItem,
                  isActive && styles.navItemActive,
                ])}
              >
                <item.icon color={tint} size={20} />
                <Text
                  style={[
                    styles.navLabel,
                    {
                      color: tint,
                      fontFamily: isActive
                        ? fontFamily.bold
                        : fontFamily.medium,
                    },
                  ]}
                >
                  {item.label}
                </Text>
              </Pressable>
            </Link>
          );
        })}
      </View>

      <View style={styles.userChip}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>{initial}</Text>
        </View>
        <View style={styles.userText}>
          <Text style={styles.userName} numberOfLines={1}>
            {userLabel}
          </Text>
          {metaLabel ? (
            <Text style={styles.userMeta} numberOfLines={1}>
              {metaLabel}
            </Text>
          ) : null}
        </View>
      </View>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    sidebar: {
      width: layout.sidebarWidth,
      borderRightWidth: 1,
      borderRightColor: colors.borderSoft,
      backgroundColor: colors.surface,
      paddingVertical: space[6],
      paddingHorizontal: space[4],
    },
    lockupWrap: { paddingHorizontal: space[2], marginBottom: space[8] },
    nav: { gap: 4, flex: 1 },
    navItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: space[3],
      paddingVertical: space[3],
      paddingHorizontal: space[3],
      borderRadius: radius.md,
    },
    navItemActive: { backgroundColor: colors.surfacePicked },
    navLabel: { fontSize: 15 },
    userChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: space[3],
      paddingTop: space[4],
      borderTopWidth: 1,
      borderTopColor: colors.borderSoft,
    },
    avatar: {
      width: 34,
      height: 34,
      borderRadius: 17,
      backgroundColor: colors.surfacePicked,
      alignItems: "center",
      justifyContent: "center",
    },
    avatarText: {
      fontFamily: fontFamily.bold,
      fontSize: 14,
      color: colors.accentSoftText,
    },
    userText: { flex: 1 },
    userName: {
      fontFamily: fontFamily.semiBold,
      fontSize: 13,
      color: colors.textPrimary,
    },
    userMeta: {
      fontFamily: fontFamily.regular,
      fontSize: 11,
      color: colors.textMuted,
    },
  });
}
